import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Drawer,
  Grid,
  makeStyles,
  MenuItem,
  Typography,
} from '@material-ui/core';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import DeleteIcon from '@material-ui/icons/Delete';
import { errorMessages } from 'common/errorMessages.strings';
import { tipoCargoDescriptions, tipoRepresentanteLegalDescriptions } from 'common/genericDescriptions';
import { tipoCargoOptions } from 'common/genericOptions';
import { TipoCargo, UsuarioParticipante, Perfil } from 'common/genericTypes';
import { CpfMask } from 'components/masks';
import { Button, Dropdown, MoreOptionsMenu, SmSpace, TextField } from 'm2m.components';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { updateUsuarios } from 'store/modules/participantes/registro';
import { addAlert } from 'store/modules/snackBar';
import { validaCpf, validaEmail } from 'utils/validation';
import { v4 as uuid } from 'uuid';

interface Props {
  isStepFulfilled(isFulfilled: boolean): void;
  isEdit?: boolean;
}

const useStyles = makeStyles(() => ({
  fixedHeight: {
    height: '150px',
  },
  fixedHeightCentered: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '206px',
  },
  wrapTxt: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  overflow: {
    overflow: 'hidden',
  },
}));

const Usuarios: React.FC<Props> = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { isStepFulfilled } = props;
  const [isEdit] = useState<boolean>(props.isEdit ?? false);
  const [openNewUsuarioDrawer, setOpenNewUsuarioDrawer] = useState<boolean>(false);
  const [usuarioEdit, setUsuarioEdit] = useState<UsuarioParticipante>();
  const [openDeleteConfirmDrawer, setOpenDeleteConfirmDrawer] = useState<boolean>(false);
  const [nome, setNome] = useState<string>('');
  const [cpf, setCpf] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [tipo, setTipo] = useState<TipoCargo>();

  const participante = useSelector((state: RootState) => state.participante);

  useEffect(() => {
    isStepFulfilled(participante.representantesLegais.length > 0 || participante.usuarios.length > 0);
  }, [participante.usuarios, props]);

  const handleSubmitClick = () => {
    if (!validaCpf(cpf)) {
      dispatch(addAlert({ severity: 'error', message: errorMessages.usuario.cpfInvalido }));
      return;
    }
    if (!validaEmail(email)) {
      dispatch(addAlert({ severity: 'error', message: errorMessages.usuario.emailInvalido }));
      return;
    }
    if (participante.usuarios.some((usuario) => usuario.email === email) || participante.representantesLegais.some((representante) => representante.email === email)) {
      dispatch(addAlert({ severity: 'error', message: errorMessages.usuario.emailDuplicado }));
      return;
    }
    if (participante.usuarios.some((usuario) => usuario.cpf === cpf) || participante.representantesLegais.some((representante) => representante.cpf === cpf)) {
      dispatch(addAlert({ severity: 'error', message: errorMessages.usuario.cpfDuplicado }));
      return;
    }

    const updatedUsuarios = participante.usuarios.concat({
      nome,
      cpf,
      email,
      perfil: Perfil.Master,
      tipo
    } as UsuarioParticipante);
    dispatch(updateUsuarios(updatedUsuarios));
    setOpenNewUsuarioDrawer(false);
    setNome('');
    setCpf('');
    setEmail('');
  };

  const handleDeleteClick = (email: string) => {
    setUsuarioEdit(participante.usuarios.find((usuario) => usuario.email === email));
    setOpenDeleteConfirmDrawer(true);
  };

  const handleDeleteItem = () => {
    const updatedUsuarios = participante.usuarios.filter(
      (usuario) => usuario.email !== usuarioEdit?.email
    );
    dispatch(updateUsuarios(updatedUsuarios));
    setOpenDeleteConfirmDrawer(false);
  };

  return (
    <>
      <Grid container spacing={3} style={{ paddingRight: '12px', width: '100%' }}>
        {participante.representantesLegais.map((representante) => (
          <Grid item sm={4} key={representante.cpf}>
            <Card variant="outlined">
              <CardHeader
                style={{ paddingBottom: 0 }}
                classes={{ content: classes.overflow }}
                title={
                  <Typography variant="h3" className={classes.wrapTxt}>
                    {representante.nome}
                  </Typography>
                }
              />
              <CardContent className={classes.fixedHeight}>
                <Grid container>
                  <Grid item md={6}>
                    <Typography variant="body2">{'Perfil:'}</Typography>
                    <Typography variant="body1">
                      {tipoRepresentanteLegalDescriptions[representante.tipo]}
                    </Typography>
                  </Grid>
                  <Grid item md={6}>
                    <Typography variant="body2">{'Email:'}</Typography>
                    <Typography variant="body1">{representante.email}</Typography>
                  </Grid>
                  <Grid item md={6}>
                    <Typography variant="body2">{'CPF:'}</Typography>
                    <Typography variant="body1">{representante.cpf}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}
        {participante.usuarios.map((usuario) => (
          <Grid item sm={4} key={usuario.cpf}>
            <Card variant="outlined">
              <CardHeader
                style={{ paddingBottom: 0 }}
                classes={{ content: classes.overflow }}
                action={ !isEdit &&
                  <MoreOptionsMenu>
                    <MenuItem onClick={() => handleDeleteClick(usuario.email)}>
                      <DeleteIcon fontSize="small" style={{ marginRight: '10px' }} />
                      {'Excluir'}
                    </MenuItem>
                  </MoreOptionsMenu>
                }
                title={
                  <Typography variant="h3" className={classes.wrapTxt}>
                    {usuario.nome}
                  </Typography>
                }
              />
              <CardContent className={classes.fixedHeight}>
                <Grid container>
                  <Grid item md={6}>
                    <Typography variant="body2">{'Perfil:'}</Typography>
                    <Typography variant="body1">
                      {usuario.tipo ? tipoCargoDescriptions[usuario.tipo] : "334"}
                    </Typography>
                  </Grid>
                  <Grid item md={6}>
                    <Typography variant="body2">{'Email:'}</Typography>
                    <Typography variant="body1">{usuario.email}</Typography>
                  </Grid>
                  <Grid item md={6}>
                    <Typography variant="body2">{'CPF:'}</Typography>
                    <Typography variant="body1">{usuario.cpf}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        ))}

        {!isEdit && (
          <Grid item sm={4}>
            <Card variant="outlined">
              <CardContent className={classes.fixedHeightCentered}>
                <Button onClick={() => setOpenNewUsuarioDrawer(true)}>
                  <div>
                    <AddCircleIcon fontSize="small" />
                    <SmSpace />
                    <Typography variant="subtitle1">{'Novo Usuário'}</Typography>
                  </div>
                </Button>
              </CardContent>
            </Card>
          </Grid>
        )}

      </Grid>
      <Drawer
        anchor="right"
        open={openNewUsuarioDrawer}
        onClose={() => setOpenNewUsuarioDrawer(false)}
        PaperProps={{ style: { padding: '40px', width: 465 } }}
      >
        <div>
          <Typography variant="h3">{'Adicionar Usuarios'}</Typography>
          <SmSpace />
          <Divider variant="fullWidth" />
          <SmSpace />
          <TextField
            label="Nome"
            value={nome}
            required
            onChange={(value) => setNome(value.replace(/[^À-ÿa-z ]/gi, ''))}
          />
          <SmSpace />
          <TextField label="CPF" value={CpfMask(cpf)} required onChange={setCpf} />
          <SmSpace />
          <TextField label="Email" value={email} required onChange={setEmail} />
          <SmSpace />
          <Dropdown
            label="Tipo"
            options={tipoCargoOptions}
            value={tipo ?? ''}
            required
            onChange={(value) => setTipo(value as TipoCargo)}
          />
          <SmSpace />
          <Button
            variant="contained"
            disabled={!(nome && cpf)}
            fullWidth
            color={'primary'}
            onClick={handleSubmitClick}
          >
            {'Adicionar Usuário'}
          </Button>
          <SmSpace />
        </div>
      </Drawer>
      <Drawer
        anchor="right"
        open={openDeleteConfirmDrawer}
        onClose={() => setOpenDeleteConfirmDrawer(false)}
        PaperProps={{ style: { padding: '40px', width: 465 } }}
      >

        {!isEdit && (
          <div>
            <Typography variant="h3">{'Excluir usuário'}</Typography>
            <SmSpace />
            <Typography variant="body2">{`Tem certeza que deseja excluir o usuário ${usuarioEdit?.nome}?`}</Typography>
            <SmSpace />
            <Divider variant="fullWidth" />
            <SmSpace />
            <Button onClick={handleDeleteItem} variant="contained" color="error" fullWidth>
              {'Sim, excluir'}
            </Button>
            <SmSpace />
            <Button onClick={() => setOpenDeleteConfirmDrawer(false)} variant="outlined" fullWidth>
              {'Cancelar'}
            </Button>
          </div>
        )}

      </Drawer>
    </>
  );
};

export default Usuarios;
